import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';

type Size = {
  width: number;
  height: number;
};

type ResizeTrackProps<T extends React.ElementType> = {
  component?: T;
  children: (size: Size) => React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<T>, 'children'>;

export default function SizeObserver<
  K extends keyof React.JSX.IntrinsicElements = 'div',
>(props: ResizeTrackProps<K>) {
  const {component = 'div', children, ...rest} = props;
  const ref = useRef<HTMLElement>(null);
  const [size, setSize] = useState<Size>({width: 0, height: 0});

  useEffect(() => {
    if (!('ResizeObserver' in window)) {
      console.error('ResizeObserver is not supported in this browser.');
      return;
    }

    const observer = new ResizeObserver((entries) => {
      try {
        const entry = entries[0];
        const {width, height} = entry.contentRect;
        setSize({width, height});
      } catch (error) {
        console.error('Error processing ResizeObserver entries:', error);
      }
    });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }
    try {
      const {width, height} = ref.current.getBoundingClientRect();
      setSize({width, height});
    } catch (error) {
      console.error('Error getting initial size:', error);
    }
  }, []);

  const Component = component as React.ElementType;

  return (
    <Component {...rest} ref={ref}>
      {children(size)}
    </Component>
  );
}
